import { Injectable, inject } from '@angular/core';

import { CasinoLobbyService, CasinoManager, ConfigProviderService, GameDataManager, LobbyItem } from '@casinocore/platform/core';

import { GameModel, GameTileConfigRender, Top10GameTileModel } from '../models/gametile.models';
import { FeatureConfig } from '../models/modular-config.models';
import { ContainerDescriptionBindings } from '../models/modular-container.models';
import { SubcategoryModel } from '../models/modular-lmt-category.models';

@Injectable({
    providedIn: 'root',
})
export class GamesReaderService {
    private readonly casinoLobbyService = inject(CasinoLobbyService);
    private readonly casinoManager = inject(CasinoManager);
    private readonly configProviderService = inject(ConfigProviderService);
    private readonly gameDataManager = inject(GameDataManager);

    /**
     * @whatItDoes filter the data which belong to the category(home) and subcategory(LMC_TopGames) passed as a parameter from the above data
     *
     * @param subcategoryId
     * @param  categoryId
     * @param lobbyType
     */
    getSubcategory(subcategoryId: string, categoryId: string, lobbyType: string): SubcategoryModel | undefined {
        const { categoriesMap } = this.getCategoriesByLobbyType(lobbyType) || {};
        if (!categoriesMap || categoriesMap?.size === 0) {
            return undefined;
        }
        const category = categoriesMap.get(categoryId);
        return category ? category.subcategories?.find((s) => s.lmtCategoryId === subcategoryId) : undefined;
    }

    getGameModels(subcategoryId: string, categoryId: string, lobbyType: string, numGames: number = 10): Top10GameTileModel[] {
        const topTenGames: Top10GameTileModel[] = [];
        const { gameMetaDataMap } = this.getCategoriesByLobbyType(lobbyType) || {};
        const subcategory: SubcategoryModel | undefined = this.getSubcategory(subcategoryId, categoryId, lobbyType);

        if (subcategory?.gamelist.length === 0) {
            return topTenGames;
        }
        subcategory?.gamelist.forEach((data: any, index: number) => {
            const filteredData = gameMetaDataMap?.get(data.game);
            const gameModelData: GameModel = this.getGameData(filteredData, lobbyType, index, subcategory.engCategoryName, data);
            if (filteredData) {
                const returnedData: Top10GameTileModel = {
                    gameId: filteredData?.game ?? '',
                    id: filteredData?.game ?? '',
                    name: filteredData?.name ?? '',
                    img: {
                        src: this.gameDataManager.formIconImageUrl(gameModelData, false),
                        defaultSrc: this.casinoManager.getGrayImageUrl(),
                        lazySrc: this.gameDataManager.formIconImageUrl(gameModelData, false),
                    },
                    footer: {
                        firstLineText: filteredData?.name ?? '',
                        secondLineText: '',
                        thirdLineText: '',
                    },
                    badges: this.getStickers(lobbyType, gameModelData),
                    game: gameModelData,
                };
                topTenGames.push(returnedData);
            }
        });
        return topTenGames.slice(0, numGames);
    }

    getGameData(filteredData: any, lobbyType: string, index: number, engCategoryName: string, data: any): GameModel {
        return {
            game: filteredData?.game ?? '',
            name: filteredData?.name ?? '',
            gameIndex: index,
            ownerId: filteredData?.ownerId ?? '',
            provider: filteredData?.provider ?? '',
            sticker: data?.sticker ?? '',
            gameId: filteredData?.game ?? '',
            icon: filteredData ? { src: this.gameDataManager.formIconImageUrl(filteredData, false) } : { src: '' },
            lobbyType,
            colSpan: index + 1,
            gameContainer: 'page container',
            gameSection: engCategoryName,
            fromCasino: true,
        };
    }

    /**
     * @whatItDoes display title subtile and icon for the respective top games or any other category configured
     *
     * @param subcategoryId
     * @param categoryId
     * @param lobbyType
     */
    getContainerDescriptionModel(subcategoryId: string, categoryId: string, lobbyType: string): ContainerDescriptionBindings {
        const subcategoryData: SubcategoryModel | undefined = this.getSubcategory(subcategoryId, categoryId, lobbyType);
        const containerDescription: ContainerDescriptionBindings = {
            iconClass: subcategoryData?.sitecoreData?.displayCategoryIcon ? subcategoryData?.sitecoreData?.attributes?.icon : '',
            titleText: subcategoryData?.categoryname,
            subtitleClass: '',
            subtitleInlineStyle: '',
            subtitleText: subcategoryData?.subTitle,
            titleInlineStyle: '',
            showIcon: subcategoryData?.sitecoreData?.attributes?.icon ? true : false,
            showTitle: subcategoryData?.categoryname ? true : false,
            showSubtitle: subcategoryData?.subTitle ? true : false,
        };
        return containerDescription;
    }

    getStickers(lobbyType: string, game: GameModel): { key: string; value: string }[] | undefined {
        if (!game.sticker) {
            return;
        }
        const stickerMap = this.casinoLobbyService.getStickerMap(lobbyType);
        if (stickerMap?.size === 0) {
            return;
        }
        if (game.sticker.indexOf('#') === -1) {
            const stickerValue: string = stickerMap.get(game.sticker);
            return [{ key: game.sticker, value: stickerValue }];
        }
        let stickerKeys = game.sticker.split('#');
        stickerKeys = this.isStickersForUk(lobbyType) ? stickerKeys : [stickerKeys[0]];
        const values = stickerKeys.map((key) => ({ key, value: stickerMap.get(key) }));
        return values.filter(({ value }) => value);
    }

    getGameTileConfigs(lobbyType: string): GameTileConfigRender {
        const featureConfig: FeatureConfig = this.configProviderService.provideFeaturesConfig();
        return {
            showMoreinfo: this.casinoManager.IsFeatureEnabledforLobbytype(lobbyType, featureConfig.enableQuickInfoLobbyType),
        };
    }

    private isStickersForUk(lobbyType: string): boolean {
        return this.casinoManager.IsFeatureEnabledforLobbytype(lobbyType, this.configProviderService.provideFeaturesConfig().stickersForUk);
    }

    /**
     * @whatItDoes To retrive all the data related to the lobby types configured in the dynacon like instantCasino, instantSlots etc,
        Filter the data based on the current lobby type which is passed as parameter below, this data contains both lmt data from platform and sitecore data configured for that lobby type
        retrive the game meta data which contains all games present for all lobbytypes configured in dynacon
    *
    * @param lobbyType
    */
    private getCategoriesByLobbyType(lobbyType: string): LobbyItem | undefined {
        if (!lobbyType) {
            return undefined;
        }
        const cachedData: Map<string, LobbyItem | undefined> = this.casinoLobbyService.lobbyTypeMaps;
        return cachedData ? cachedData.get(lobbyType?.trim()) : undefined;
    }
}
